import { computedFrom } from "aurelia-binding";
import { autoinject, transient } from "aurelia-dependency-injection";
import { I18N } from "aurelia-i18n";
import { Router } from "aurelia-router";
import { GenesisRouterConstants } from "../../../../configuration/router.constants";
import { RightShipDialogServiceConstants } from "../../../../constants/basics/rightship-dialog.constants";
import { type IOrderMetaPlugin, OrderModel, OrderStatusEnum } from "../../../../models/rightstore/rightstore.models";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { NotificationClientService, NotificationType } from "../../../../services/basics/notification-client.service";
import { RightStoreService } from "../../../../services/rightstore/rightstore.service";
import { RulesClientService } from "../../../../services/rules/rules-client.service";
import { FillFormLaterConfirmationDialogModel } from "./dialogs/fill-form-later-confirmation-dialog/fill-form-later-confirmation-dialog";
import { Reflection } from "../../../../utilities/reflection";
import { SignalFunctions } from "../../../../services/basics/signal-functions";
import { SignalClientService } from "@rightship-tech/common-ui/services/basic/signal-client.service";
import { ConfigService } from "@rightship-tech/common-ui/services/basic/config.service";

@autoinject()
@transient()
export class Order implements FillFormLaterConfirmationDialogModel {

    private displayFillFormLaterWhenRedirect: boolean = true;

    vesselId: number;
    companyId: number;
    vesselIds: number[];

    composeVM: any;
    ruleCollectionDisplayName: string;
    order: OrderModel;
    public rightStoreUri: string;

    @computedFrom("metaVM", "metaVM.viewTitle")
    get viewTitle(): string {
        return this.metaVM ? this.metaVM.viewTitle : null;
    }

    @computedFrom("composeVM", "composeVM.currentViewModel")
    get metaVM(): IOrderMetaPlugin {
        const vm = this.composeVM && this.composeVM.currentViewModel ? this.composeVM.currentViewModel : null;
        return vm;
    }

    @computedFrom(Reflection.getPropertyPath<Order>(x => x.order))
    get viewModel(): string {
        if (!this.order) {
            return '';
        }
        return this.order.orderStatusId == OrderStatusEnum.PendingMeta ? this.order.product.viewModel : 'processing';
    }

    constructor(private i18N: I18N,
        private dialogService: DialogClientService,
        private notificationService: NotificationClientService,
        private rightStoreService: RightStoreService,
        private ruleService: RulesClientService,
        private signalClientService: SignalClientService,
        private router: Router, private configService: ConfigService) {
            this.rightStoreUri = this.configService.config.urls.rightStoreApiUrl;
        }

    async activate(args: { guid: string }) {
        try {
            const orderResponse = await this.rightStoreService.getOrder(args.guid);
            this.order = orderResponse.order;

            this.vesselId = args["vesselId"] ? args["vesselId"] : args["vesselid"] ? args["vesselid"] : null;
            this.companyId = args["companyId"] ? args["companyId"] : args["companyid"] ? args["companyid"] : null;
            this.vesselIds = (args["vesselIds"] || "").split(",").map(Number);

            if (this.order == null) {
                this.router.navigateToRoute(GenesisRouterConstants.RouteNames.StatusCode404);
                return;
            }

            if (this.order.product.variantAttributes['ruleCollectionId']) {
                //Get Rule Collection Display Name for email name and page title
                const ruleDisplayName = await this.ruleService.getRuleCollectionDisplayName(this.order.product.variantAttributes['ruleCollectionId']);
                this.ruleCollectionDisplayName = ruleDisplayName;
            }
            //order found but vm is not correct, redirect to correct vm
            if (this.order.orderStatusId == OrderStatusEnum.Fulfilled) {
                const orderUrlResponse = await this.rightStoreService.getOrderUrl(this.order.orderGuid);
                if (orderUrlResponse && orderUrlResponse.shouldNavigateToProduct) {
                    this.router.navigate(orderUrlResponse.url, { replace: true, trigger: true });
                    return;
                }
            }
            this.signalClientService.subscribe(SignalFunctions.refreshOrder, this.refreshOrder);
        } catch (e) {
            this.notificationService.show(this.i18N.tr('error-text'), "Failed to validate order", NotificationType.Error);
            console.error(e);
        }
    }

    deactivate() {
        this.signalClientService.unsubscribe(SignalFunctions.refreshOrder, this.refreshOrder);
    }

    async submit() {
        const isValid = await this.metaVM.isValid();
        if (isValid && this.metaVM.canSubmit && !this.metaVM.disableSubmit) {
            const meta = JSON.stringify(this.metaVM.getMetaModel());
            try {
                const response = await this.rightStoreService.saveOrderMeta(this.order.orderGuid.toString(), meta);
                this.notificationService.show(this.i18N.tr('successfully-saved-text'), "Order completed", NotificationType.Success);

                const orderResponse = await this.rightStoreService.getOrder(response.orderGuid);
                const orderUrlResponse = await this.rightStoreService.getOrderUrl(response.orderGuid);
                this.order = orderResponse.order;
                this.displayFillFormLaterWhenRedirect = false;
                if (orderUrlResponse.url.startsWith(location.origin)) {
                    this.router.navigate(orderUrlResponse.url.replace(location.origin, ""), { replace: true, trigger: true });
                }
                else {
                    location.href = orderUrlResponse.url;
                }
            }
            catch (e) {
                this.displayFillFormLaterWhenRedirect = true;
                this.notificationService.show(this.i18N.tr('error-text'), "Failed to update order", NotificationType.Error);
            }
        } else {
            this.displayFillFormLaterWhenRedirect = true;
        }
    }

    refreshOrder = (args: { orderGuid: string }) => {
        if (this.order.orderGuid == args.orderGuid) {
            const promise1 = this.rightStoreService.getOrder(args.orderGuid);
            const promise2 = this.rightStoreService.getOrderUrl(args.orderGuid);
            Promise.all([promise1, promise2]).then(results => {
                const orderResponse = results[0];
                const orderUrlResponse = results[1];
                this.order = orderResponse.order;

                if (orderUrlResponse.url.startsWith(location.origin)) {
                    this.router.navigate(orderUrlResponse.url.replace(location.origin, ""), { replace: true, trigger: true });
                }
                else {
                    location.href = orderUrlResponse.url;
                }
            })
        }
    }

    doLater() {
        this.displayFillFormLaterWhenRedirect = false;
        this.openFillFormLaterDialog(null, () => {
            this.displayFillFormLaterWhenRedirect = true;
        });
    }

    openFillFormLaterDialog(onConfirm: () => void, onCancel: () => void) {
        this.dialogService.show(RightShipDialogServiceConstants.URLs.FillFormLaterConfirmationDialog, {
            order: this.order,
            viewTitle: this.viewTitle,
            viewModel: this.viewModel,
            vesselId: this.metaVM && ('vesselId' in this.metaVM) ? (<any>this.metaVM).vesselId : this.vesselId,
            onConfirm: onConfirm,
            onCancel: onCancel,
        }, true);
    }

    async canDeactivate() {
        if (this.metaVM.canSubmit && this.displayFillFormLaterWhenRedirect) {
            const result = await new Promise<boolean>((resolve) => {
                this.openFillFormLaterDialog(() => { resolve(true); }, () => { resolve(false); })
            });
            return result;
        }
        else {
            return true;
        }
    }
}